// @ts-ignore
import React from "react";

import { css } from "@compiled/react";

import { media } from "@atlaskit/primitives/responsive";

import { PictureProps } from "@/components/wpl/image/src/image";
import {
    PullQuote,
    PullQuoteProps
} from "@/components/wpl/pull-quote/src/pull-quote";
import {
    TabGroup,
    IndividualTabPropsCustom,
    TabColorProps
} from "@/components/wpl/tab-group/src/tab-group";
import { ProductKeyTypes } from "@/libraries/wpl/foundations/src/data-event-props";
import { Row, Layout } from "@/libraries/wpl/foundations/src/grid";
import ImageChecklistSide from "@/patterns/image-checklist-side/src/image-checklist-side";
import SimpleImageTextSide from "@/patterns/wpl/simple-image-text-side/src";

const tabCustomStyles = css({
    "marginTop": "40px",
    "marginBottom": "60px",
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group .tab-group-inner": {
        marginBlockEnd: "20px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group .tab-group-inner button": {
        "border": "1px solid transparent",
        "fontSize": "0.875rem",
        "@media (min-width: 1024px)": {
            fontSize: "1.25rem"
        }
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".simple-image-text-side-tabs": {
        padding: 0
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    '.tab-group .tab-group-inner button[aria-selected="false"]': {
        // eslint-disable-next-line local/ensure-wac-token-usage
        color: "var(--lp-tabs-unselected, #6b778c)"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group .tab-group-inner button:hover": {
        borderColor: "var(--lp-tabs-hover, transparent)"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    ".tab-group .tab-panel h3": {
        "color": "#253858",
        "fontSize": "1.25rem",
        "@media (min-width: 758px)": {
            fontSize: "1.5rem"
        }
    }
});
const tabSectionTitleStyles = css({
    "textAlign": "center",
    "fontSize": "1.5rem",
    "marginBlockEnd": "40px",
    "fontWeight": "500",
    "color": "#253858",
    "@media(min-width: 768px)": {
        fontSize: "2.25rem"
    }
});
const singleTabStyles = css({
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: "0 60px",
    [media.below.sm]: {
        padding: "0 32px"
    },
    // eslint-disable-next-line @atlaskit/ui-styling-standard/no-nested-selectors
    h2: {
        fontSize: "2rem",
        [media.below.sm]: {
            fontSize: "1.5rem"
        }
    }
});

const pullQuotesContainerStyles = css({
    paddingBlockStart: "16px"
});
export interface PaidTabProps {
    textLabel: string;
    id: string;
    title: string;
    listItems?: (string | JSX.Element)[];
    body?: string;
    picture: PictureProps;
    pullQuote?: PullQuoteProps;
}

/*
Use either `tabs` or `tabsConfig` to add tabs

The original approach was with `tabs` which requires passing in a component created separately.

Going forward, `tabsConfig` allows for passing all tabs info via props like the other paid lp sections.
The component needed for `TabGroup` is generated in this file based on the props passed in
*/
export interface PaidTabsProps {
    headerText: string;
    tabStyles: TabColorProps;
    tabs?: IndividualTabPropsCustom[];
    tabsConfig?: PaidTabProps[];
    dataProductKey: ProductKeyTypes;
    layout?: Layout;
    pullQuoteIsUsingTextQuotationMarks?: boolean;
}

const renderChecklist = (tab: PaidTabProps): JSX.Element => {
    return (
        <ImageChecklistSide
            title={tab.title}
            listItems={tab.listItems ?? []}
            imageSrc={tab.picture.image.src}
            imageAlt={tab.picture.image.alt}
        />
    );
};

const renderBodyText = (tab: PaidTabProps): JSX.Element => {
    return (
        <SimpleImageTextSide
            headerText={tab.title}
            headerType="h3"
            bodyText={tab.body}
            imageLink={tab.picture.image.src}
            imageAltText={tab.picture.image.alt}
            alignItems={"top"}
            rowProps="4-8"
            className="simple-image-text-side-tabs"
        />
    );
};

const generateTabs = (
    tabsViaProps: PaidTabProps[]
): IndividualTabPropsCustom[] => {
    const transformedTabs: IndividualTabPropsCustom[] = tabsViaProps.map(
        (tab) => {
            return {
                textLabel: tab.textLabel,
                id: tab.id,
                component: (
                    <React.Fragment>
                        {tab.listItems?.length
                            ? renderChecklist(tab)
                            : renderBodyText(tab)}
                        {tab.pullQuote && (
                            <div css={pullQuotesContainerStyles}>
                                <PullQuote
                                    logoSrc={tab.pullQuote.logoSrc}
                                    logoAltText={tab.pullQuote.logoAltText}
                                    quoteBlurb={tab.pullQuote.quoteBlurb || ""}
                                    quoteCompany={
                                        tab.pullQuote.quoteCompany || ""
                                    }
                                    quoteName={tab.pullQuote.quoteName || ""}
                                    isShowBorder={
                                        tab.pullQuote.isShowBorder ?? false
                                    }
                                    isUsingTextQuotationMarks={
                                        tab.pullQuote.isUsingTextQuotationMarks
                                    }
                                />
                            </div>
                        )}
                    </React.Fragment>
                )
            };
        }
    );

    return transformedTabs;
};

export const PaidTabs = ({
    headerText,
    tabStyles,
    tabs,
    tabsConfig,
    dataProductKey,
    layout = "8"
}: PaidTabsProps) => {
    tabs = tabsConfig ? generateTabs(tabsConfig) : tabs ?? [];

    return (
        <div css={tabCustomStyles}>
            {tabs.length === 1 ? (
                <div css={singleTabStyles}>{tabs[0]?.component}</div>
            ) : (
                <>
                    <Row layout={layout}>
                        <h2 css={tabSectionTitleStyles}>{headerText}</h2>
                    </Row>
                    {/* @ts-ignore */}
                    <TabGroup
                        tabContentType="custom"
                        tabs={tabs}
                        id="tabs"
                        label="tabs"
                        colors={tabStyles}
                        dataProductKey={dataProductKey}
                    />
                </>
            )}
        </div>
    );
};
